/**
 * GST Invoice Configuration & Utilities
 *
 * Single source of truth for:
 *  - Company / supplier details
 *  - Bank details
 *  - GST calculation (CGST+SGST for intra-state AP, IGST for other states)
 *  - Amount in words (Indian numbering system)
 *  - Currency formatters
 *  - SAC code
 */

// ─── Supplier Configuration ───────────────────────────────────────────────────
export const COMPANY = {
  name:        'EVOLVE X INNOVATIONS',
  legalName:   'Evolve X Innovations',
  gstin:       '37BABPP4434R1Z4',
  pan:         'BABPP4434R',
  state:       'Andhra Pradesh',
  stateCode:   '37',
  address:     'Bhoomatha Thalluri Heights, Kankipadu',
  city:        'Vijayawada – 521151',
  fullAddress: 'Bhoomatha Thalluri Heights, Kankipadu, Vijayawada – 521151, Andhra Pradesh',
  email:       'connect@evolvexinnovations.org',
  supportEmail:'connect@evolvexinnovations.org',
  website:     'https://www.theskillgenie.com',
  phone:       '+91 62815 97586',
};

// ─── Bank Details ─────────────────────────────────────────────────────────────
export const BANK = {
  accountName:   'Evolve X Innovations',
  accountNumber: '',
  ifsc:          '',
  bankName:      'State Bank of India',
  branch:        'Vijayawada Main Branch',
  upi:           '',
};

// ─── Constants ────────────────────────────────────────────────────────────────
export const SAC_CODE      = '998315';
export const GST_RATE      = 0.18;
export const CGST_RATE     = 0.09;
export const SGST_RATE     = 0.09;
export const SUPPLIER_STATE = 'Andhra Pradesh';
export const EVOLVEX_LOGO_URL =
  'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/college_uploads/evolvex-logo.jpeg';

// ─── GST Calculator ───────────────────────────────────────────────────────────
/**
 * Resolves CGST+SGST (intra-state) or IGST (inter-state).
 * Supplier state is always Andhra Pradesh.
 *
 * @param {number} taxableValue  Pre-tax amount in INR
 * @param {string} customerState Full state name as stored in DB (e.g. "Karnataka")
 * @returns {{ type, cgst, sgst, igst, totalTax }}
 */
export function computeGST(taxableValue, customerState) {
  const tv    = parseFloat(taxableValue) || 0;
  const intra = (customerState || '').trim().toLowerCase() === SUPPLIER_STATE.toLowerCase();

  if (intra) {
    const half = parseFloat((tv * CGST_RATE).toFixed(2));
    return { type: 'CGST_SGST', cgst: half, sgst: half, igst: 0, totalTax: parseFloat((half * 2).toFixed(2)) };
  }
  const full = parseFloat((tv * GST_RATE).toFixed(2));
  return { type: 'IGST', cgst: 0, sgst: 0, igst: full, totalTax: full };
}

// ─── Amount in Words (Indian Numbering) ──────────────────────────────────────
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function _chunk(n) {
  if (n === 0)   return '';
  if (n < 20)    return ONES[n];
  if (n < 100)   return TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + ONES[n % 10] : '');
  return ONES[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + _chunk(n % 100) : '');
}

function _n2w(n) {
  if (n === 0)     return 'Zero';
  if (n < 1000)    return _chunk(n);
  if (n < 1e5)     return _chunk(Math.floor(n / 1000)) + ' Thousand' + (n % 1000  ? ' ' + _chunk(n % 1000)              : '');
  if (n < 1e7)     return _chunk(Math.floor(n / 1e5))  + ' Lakh'     + (n % 1e5   ? ' ' + _n2w(n % 1e5)                 : '');
  return                  _chunk(Math.floor(n / 1e7))  + ' Crore'    + (n % 1e7   ? ' ' + _n2w(n % 1e7)                 : '');
}

/**
 * Converts a numeric INR amount to Indian English words.
 * e.g. 176882 → "Rupees One Lakh Seventy Six Thousand Eight Hundred Eighty Two Only"
 */
export function amountInWords(amount) {
  const n     = Math.floor(parseFloat(amount) || 0);
  const paise = Math.round(((parseFloat(amount) || 0) - n) * 100);
  let words   = 'Rupees ' + (_n2w(n) || 'Zero');
  if (paise > 0) words += ' and ' + _n2w(paise) + ' Paise';
  return words + ' Only';
}

// ─── Currency Formatters ──────────────────────────────────────────────────────
/** e.g. 1234567.5 → "₹12,34,567.50" */
export const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** e.g. 1234567 → "₹12,34,567" (no decimals) */
export const fmtINRInt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

/** e.g. 1234567 → "12,34,567" (no ₹ symbol) */
export const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

// ─── Date Formatter ───────────────────────────────────────────────────────────
export const fmtInvoiceDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── Terms & Conditions ───────────────────────────────────────────────────────
export const QUOTATION_TERMS = [
  '1. Quotation is valid for 15 days from the date of issue.',
  '2. Prices are exclusive of GST unless otherwise stated.',
  '3. GST shall be charged as applicable under Indian GST Law.',
  '4. Payment once made is non-refundable.',
  '5. Subscription will be activated only after successful payment confirmation.',
  '6. Services are governed by the Terms of Service published by EVOLVE X INNOVATIONS.',
  '7. Credits once purchased cannot be exchanged for cash.',
  '8. Unused credits are governed by the subscribed plan validity.',
  '9. Support is available during business hours.',
  '10. Jurisdiction: Vijayawada, Andhra Pradesh.',
];

export const INVOICE_DECLARATION =
  'We declare that this invoice shows the actual price of the services described ' +
  'and that all particulars are true and correct.';

// ─── Invoice Terms & Conditions (13 clauses) ──────────────────────────────────
export const INVOICE_TERMS = [
  '1. Prices mentioned are exclusive of GST unless explicitly stated. GST shall be charged as applicable under the Goods and Services Tax Act, India.',
  '2. Subscription, credits, and services will be activated only after successful payment confirmation and receipt of cleared funds.',
  '3. The Skill Genie is a cloud-based Software-as-a-Service (SaaS) platform. Access is provided on a subscription basis and remains valid only for the purchased plan duration.',
  '4. All subscription fees, platform credits, implementation charges, setup fees, and any other payments made to EVOLVE X INNOVATIONS are non-refundable and non-transferable.',
  '5. No cancellation is permitted after payment confirmation. Once a subscription, credit package, or enterprise plan has been purchased and activated, it cannot be cancelled or reversed.',
  '6. Purchased credits, subscriptions, and platform licenses cannot be exchanged for cash, transferred to another institution, or adjusted against future purchases unless explicitly approved in writing by EVOLVE X INNOVATIONS.',
  '7. Any unused credits, licenses, or subscription validity remaining at the end of the purchased period shall automatically expire as per the subscribed plan and shall not be eligible for refund or extension.',
  '8. The customer is responsible for providing accurate billing information, including GSTIN, billing address, and contact details before invoice generation.',
  '9. Refund & Cancellation Policy: All payments made towards subscriptions, enterprise plans, implementation services, platform credits, assessments, or any other services are final. No Refunds, No Cancellations, and No Transfers are permitted after successful payment, except where required under applicable law.',
  '10. Technical support shall be provided during official business hours in accordance with the subscribed plan.',
  '11. Any statutory taxes, duties, levies, or government charges introduced after the date of invoice shall be payable by the customer wherever applicable.',
  '12. This invoice is generated electronically and does not require a physical signature.',
  '13. All disputes arising from this transaction shall be subject to the exclusive jurisdiction of the competent courts at Vijayawada, Andhra Pradesh, India.',
];

// ─── Reusable PDF Section Renderer ────────────────────────────────────────────
/**
 * Renders T&C + Declaration + Authorized Signatory into a jsPDF doc.
 * Handles automatic page breaks — calls footerFn before every new page.
 *
 * @param {object}   doc           jsPDF instance
 * @param {number}   startY        Starting Y position (mm)
 * @param {object}   opts
 * @param {number}   opts.W        Page width (mm)
 * @param {number}   opts.H        Page height (mm)
 * @param {number}   opts.M        Margin (mm)
 * @param {number}   opts.CW       Content width (mm)
 * @param {number[]} opts.BRAND    Header bar RGB
 * @param {number[]} opts.BRANDB   Accent RGB
 * @param {number[]} opts.DARK     Body text RGB
 * @param {number[]} opts.MID      Secondary text RGB
 * @param {string[]} opts.terms    Array of term strings
 * @param {string}   opts.declaration  Declaration body text
 * @param {string}   opts.companyName  Name shown in signatory line
 * @param {Function} [opts.footerFn]   Called with (doc) just before each addPage()
 * @returns {number} Y position after all content
 */
export function renderTermsAndSignatory(doc, startY, {
  W, H, M, CW,
  BRAND, BRANDB, DARK, MID,
  terms,
  declaration,
  companyName,
  footerFn,
}) {
  const FOOTER_RESERVE = 22;
  const PAGE_BOTTOM = H - FOOTER_RESERVE;
  let y = startY;

  const ensureSpace = (needed) => {
    if (y + needed > PAGE_BOTTOM) {
      if (footerFn) footerFn(doc);
      doc.addPage();
      y = M;
    }
  };

  // ── Section heading bar
  ensureSpace(16);
  doc.setFillColor(...BRAND);
  doc.rect(M, y, CW, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS & CONDITIONS', M + 4, y + 5.5);
  y += 10;

  // ── Term items with auto-wrap + page-break
  const WRAP_W = CW - 10;
  const LINE_H = 4.6;
  doc.setFontSize(6.8);

  terms.forEach((term) => {
    const wrapped = doc.splitTextToSize(term, WRAP_W);
    const blockH = wrapped.length * LINE_H + 3;
    ensureSpace(blockH + 2);
    doc.setTextColor(...MID);
    doc.setFont('helvetica', 'normal');
    doc.text(wrapped, M + 4, y, { lineHeightFactor: 1.35 });
    y += blockH;
  });

  y += 6;

  // ── Declaration + Authorized Signatory box
  const DECL_H = 30;
  ensureSpace(DECL_H + 4);
  doc.setFillColor(249, 250, 251);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, y, CW, DECL_H, 2, 2, 'FD');

  doc.setTextColor(...BRANDB);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('DECLARATION', M + 4, y + 5);
  doc.setDrawColor(220, 220, 220);
  doc.line(M + 4, y + 6.5, M + CW - 4, y + 6.5);

  doc.setTextColor(...MID);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  const splitDecl = doc.splitTextToSize(declaration, CW - 72);
  doc.text(splitDecl, M + 4, y + 11);

  const sigX = M + CW - 66;
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(`For ${companyName}`, sigX, y + 13);
  doc.setDrawColor(160, 160, 160);
  doc.line(sigX, y + 20, sigX + 58, y + 20);
  doc.setTextColor(...MID);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Authorized Signatory', sigX, y + 24.5);

  return y + DECL_H + 4;
}
